import { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../../../../app/hooks';
import { useBoundedPoll } from '../../../../../common/hooks/useBoundedPoll';
import { GRACE_WINDOW_MS, useGraceWindow } from '../../../../../common/hooks/useGraceWindow';
import { useLatestRequestGuard } from '../../../../../common/hooks/useLatestRequestGuard';
import { isTerminalFailure, TASK_TRACKER_STATUS } from '../../../../../utils/constants/taskTrackerStatus';
import { AION_INGEST_LITIGATION_CLASSIFICATION_DATA } from '../../../DashboardModules/Overview/components/ManagementProfile/components/managementUserProfileUtils';
import { getAllTaskTrackerData } from '../../../../../store/investor/investorApiHelper';
import { DATA_FETCH_STATUS } from '../../../../../store/dashboard/constants';
import { LEGAL_CASES_GRACE_ATTEMPTS, LEGAL_CASES_PENDING_ATTEMPTS, LEGAL_CASES_POLL_INTERVAL_MS } from './legalCasesConstants';

type PollingPhase = 'pending' | 'grace';

interface LegalCasesPollingResult {
    isPolling: boolean;
    isFetchFailed: boolean;
}

/**
 * Owns the litigation-tracker polling ladder for the Legal Cases tab:
 *   - PDF report route (`isPDFReport`) -> never polls, never shows the loader. `TabSelector`
 *     doesn't mount there, so the redux tracker map is always empty/stale for that render — a
 *     print surface must render whatever it has immediately, not enter the ladder.
 *   - FAILED/FAILURE tracker status -> stop polling, no loader. Shows the failure indicator only
 *     when there are no case rows to fall back on; with last-known rows the header just goes quiet.
 *   - TODO/TODO_ASYNC/absent -> keep polling on a 5s cadence, show the loader.
 *   - COMPLETE -> render rows if present, and keep polling for one more GRACE_WINDOW_MS window
 *     (armed once per assesseeOrgId, and re-armed on an org switch between two COMPLETE orgs) so
 *     rows landing moments after COMPLETE aren't missed. The indicator stays up for the whole
 *     window regardless of whether rows are already present — the table itself always renders,
 *     so there is nothing left for the loader to gate.
 *
 * Before the BE fix ships, litigation rows can sit TODO_ASYNC forever — the pending poll's own
 * attempt budget is what eventually stops the loader in that case (not just the network calls).
 *
 * The redux tracker map is a one-shot snapshot taken at page load (see TabSelector.tsx), so a run
 * that completes mid-page would otherwise go unnoticed until the pending budget itself runs out.
 * `refreshTracker` re-reads the tracker on every pending poll tick to close that gap — see its own
 * doc below for why it's hook-local instead of dispatched to redux.
 *
 * The redux snapshot's `status === 'Processing'` is the live "run in flight" signal while the
 * snapshot (and this hook's own re-read) may still read COMPLETE — polus never sends 'Processed',
 * so only 'Processing' is meaningful, and treating it as pending alongside the tracker avoids
 * arming the grace window early.
 *
 * `initialiseHeadlinePanelPolling` starts an unbounded headline-panel interval, so it is only
 * triggered on the live run signal (`legalCasesStatus === Processing`), once per transition into
 * that state — not on every tracker-derived pending render (TODO_ASYNC/absent included).
 */
export function useLegalCasesPolling({
    assesseeOrgId,
    legalCasesStatus,
    hasResponse,
    hasCaseRows,
    fetchData,
    initialiseHeadlinePanelPolling,
    isPDFReport = false,
}: {
    assesseeOrgId: string;
    legalCasesStatus?: string;
    hasResponse: boolean;
    hasCaseRows: boolean;
    fetchData: (isPollingCall: boolean) => void;
    initialiseHeadlinePanelPolling?: Function;
    isPDFReport?: boolean;
}): LegalCasesPollingResult {
    const reduxTrackerStatus = useAppSelector(
        (state) => state.investor.taskTrackerMap?.[AION_INGEST_LITIGATION_CLASSIFICATION_DATA]?.status,
    );

    // Hook-local, org-stamped re-read of the tracker (see refreshTracker below). Falls back to
    // the redux snapshot until THIS org's own tracker call has resolved — preserving the correct
    // FAILED classification before any tick, and dropping a stale org's status on a switch.
    const [tracker, setTracker] = useState<{ orgId: string; status?: string }>({ orgId: '' });
    const litigationTrackerStatus = tracker.orgId === assesseeOrgId ? tracker.status : reduxTrackerStatus;

    const isTerminal = isTerminalFailure(litigationTrackerStatus);
    // Budget-spent is deliberately excluded — the tracker never reported FAILED there, so it is
    // not a failure, just an abandoned wait. PDF route never shows the indicator at all. With
    // last-known rows already on screen the header goes quiet instead of showing the indicator —
    // only a terminal failure with nothing to show earns the "Not able to fetch" treatment.
    const isFetchFailed = !isPDFReport && isTerminal && !hasCaseRows;
    const isComplete =
        hasResponse && litigationTrackerStatus === TASK_TRACKER_STATUS.COMPLETE && legalCasesStatus !== DATA_FETCH_STATUS.PROCESSING;
    const phase: PollingPhase = isComplete ? 'grace' : 'pending';

    // Set once the pending poll's own attempt budget is spent without ever reaching COMPLETE —
    // the loader must not spin forever on a step that never resolves.
    const [pendingCapReached, setPendingCapReached] = useState(false);

    // Resets the pending-cap flag on a genuine org switch — a stale cap flag from the previous
    // org must not leak into the next one. The grace window itself is reset internally by
    // useGraceWindow, keyed on the same assesseeOrgId.
    useEffect(() => {
        setPendingCapReached(false);
    }, [assesseeOrgId]);

    // Arms the window exactly once per (assesseeOrgId, COMPLETE) pair — a later re-render that
    // still reads COMPLETE for the SAME org (e.g. a modulePollingList identity flip) must not
    // restart the countdown, but switching to a different org that is also already COMPLETE
    // re-arms a fresh window.
    const { windowExpired } = useGraceWindow({
        isComplete,
        resetKey: assesseeOrgId,
        windowMs: GRACE_WINDOW_MS,
        enabled: !isPDFReport,
        onArm: () => setPendingCapReached(false),
    });

    // Fires the headline-panel poll only on the live "run in flight" signal, once per transition
    // into it — not on every tracker-derived pending render (TODO_ASYNC/absent included).
    const headlinePanelTriggeredRef = useRef(false);
    useEffect(() => {
        const isLive = !isPDFReport && legalCasesStatus === DATA_FETCH_STATUS.PROCESSING;
        if (isLive && !headlinePanelTriggeredRef.current) {
            headlinePanelTriggeredRef.current = true;
            initialiseHeadlinePanelPolling?.();
        } else if (!isLive) {
            headlinePanelTriggeredRef.current = false;
        }
    }, [legalCasesStatus, initialiseHeadlinePanelPolling, isPDFReport]);

    const shouldPoll = !isPDFReport && !isTerminal && (!isComplete || !windowExpired);

    // Re-reads the litigation tracker on every pending poll tick so a run that completes mid-page
    // is noticed within one tick, instead of only once the pending poll's own budget runs out.
    // Hook-local rather than dispatched to redux: taskTrackerMap is TabSelector-owned and read by
    // unrelated gates (management-profile spinner, modulePollingList sweeps, its own org-switch
    // reset) — writing it from here would race TabSelector's own writes and silently widen this
    // change's blast radius past Legal Cases.
    const trackerRequestGuard = useLatestRequestGuard();
    const refreshTracker = () => {
        const token = trackerRequestGuard.startRequest();
        const requestOrgId = assesseeOrgId;
        getAllTaskTrackerData(
            { orgId: requestOrgId },
            {
                onSuccess: (taskTrackerData) => {
                    if (!trackerRequestGuard.isCurrent(token)) return;
                    const status = taskTrackerData?.[AION_INGEST_LITIGATION_CLASSIFICATION_DATA]?.status;
                    setTracker((prev) => (prev.orgId === requestOrgId && prev.status === status ? prev : { orgId: requestOrgId, status }));
                },
                // Keep the last known status on error — the redux snapshot remains the floor.
                onError: () => {},
            },
            true, // noToast: a pending poll's own tracker re-read must never toast (A-35)
        );
    };

    const pollTick = (isPollingCall: boolean) => {
        fetchData(isPollingCall);
        if (phase === 'pending' && !isTerminal) refreshTracker();
    };

    useBoundedPoll(pollTick, shouldPoll, () => setPendingCapReached(true), {
        intervalMs: LEGAL_CASES_POLL_INTERVAL_MS,
        maxAttempts: phase === 'pending' ? LEGAL_CASES_PENDING_ATTEMPTS : LEGAL_CASES_GRACE_ATTEMPTS,
        fireImmediately: false,
        stickyAttemptsKey: `${assesseeOrgId}:${phase}`,
    });

    const isPolling = shouldPoll && !pendingCapReached;

    return { isPolling, isFetchFailed };
}
